/**
 * BoundaryCurve - 3D boundary curve visualization
 * 
 * Renders 3D boundary curve from draping operation as a fat line overlay
 * on the foot scan mesh. Uses Three.js LineLoop for maximum compatibility.
 */

import React, { useMemo } from 'react'
import * as THREE from 'three'

interface BoundaryCurveProps {
  /** 3D boundary points [[x,y,z], [x,y,z], ...] */
  boundaryCurve3d: number[][]
  /** Line color (default: #66BB6A) */
  color?: string
  /** Line width (default: 3) */
  linewidth?: number
  /** Opacity (default: 1.0) */
  opacity?: number
}

/**
 * BoundaryCurve component - renders 3D boundary as a line loop
 */
export const BoundaryCurve: React.FC<BoundaryCurveProps> = ({
  boundaryCurve3d,
  color = '#66BB6A',
  linewidth = 3,
  opacity = 1.0
}) => {
  const points = useMemo(() => {
    if (boundaryCurve3d.length < 2) return []
    
    // Convert to Vector3 array and close the loop
    const pts = boundaryCurve3d.map(([x, y, z]) => new THREE.Vector3(x, y, z))
    pts.push(pts[0].clone()) // Close the loop
    return pts
  }, [boundaryCurve3d])

  const geometry = useMemo(() => {
    if (points.length < 2) return null
    
    const geometry = new THREE.BufferGeometry()
    const positions = new Float32Array(points.flatMap(p => [p.x, p.y, p.z]))
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    return geometry
  }, [points])

  if (!geometry || points.length < 2) {
    return null
  }

  return (
    <lineLoop geometry={geometry}>
      <lineBasicMaterial
        color={color}
        linewidth={linewidth}
        opacity={opacity}
        transparent={opacity < 1.0}
      />
    </lineLoop>
  )
}
